import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import AuthWrapper from './components/AuthWrapper';
import AdminAuthWrapper from './components/AdminAuthWrapper';
import { initializeFormCacheClear } from './utils/clearFormCache';

// Import Layout Components
import Header from './components/Header_Simple';
import Footer from './components/Footer_Simple';

// Import Page Components
import HomePage from './pages/HomePage';
import StudentFormPage from './pages/StudentFormPage';
import UniversityFeesPage from './pages/UniversityFeesPage';
import StatusCheckPage from './pages/StatusCheckPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PartnerLoginPage from './pages/PartnerLoginPage';
import PartnerDashboardPage from './pages/PartnerDashboardPage';
import AdminPartnerRegistrationPage from './pages/AdminPartnerRegistrationPage';
import AdminPartnersManagementPage from './pages/AdminPartnersManagementPage';
import GoogleLoginTestPage from './pages/GoogleLoginTestPage';
import AuthPrototypePage from './pages/AuthPrototypePage';
import AuthDebugPage from './pages/AuthDebugPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import PaymentTestPage from './pages/PaymentTestPage';

// Scroll restoration component
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Clear form cache on app startup to prevent data loss incidents
  useEffect(() => {
    console.log('App mounted');
    
    // CRITICAL: Clear all cached form data to prevent submitted applications from being overwritten
    initializeFormCacheClear();
    
    return () => console.log('App unmounted');
  }, []);

  console.log('App rendering with darkMode:', darkMode);

  return (
    <Router>
      <ErrorBoundary>
        <div className={`flex flex-col min-h-screen font-sans ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
          <ScrollToTop />
          {!hideHeader && <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />}
          
          {/* Main content area where pages will be rendered */}
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<HomePage darkMode={darkMode} />} />
              <Route path="/university-fees" element={<UniversityFeesPage darkMode={darkMode} setHideHeader={setHideHeader} />} />
              <Route path="/student-form" element={
                <AuthWrapper requireAuth={true} darkMode={darkMode}>
                  <StudentFormPage darkMode={darkMode} />
                </AuthWrapper>
              } />
              <Route path="/test/student-form" element={
                <AuthWrapper requireAuth={true} darkMode={darkMode}>
                  <StudentFormPage darkMode={darkMode} />
                </AuthWrapper>
              } />
              <Route path="/status-check" element={
                <AuthWrapper requireAuth={false} darkMode={darkMode}>
                  <StatusCheckPage darkMode={darkMode} />
                </AuthWrapper>
              } />
              <Route path="/admin-login" element={<AdminLoginPage darkMode={darkMode} />} />
              <Route path="/admin-dashboard" element={
                <AdminAuthWrapper darkMode={darkMode}>
                  <AdminDashboardPage darkMode={darkMode} />
                </AdminAuthWrapper>
              } />
              <Route path="/admin/partner-registration" element={
                <AdminAuthWrapper darkMode={darkMode}>
                  <AdminPartnerRegistrationPage darkMode={darkMode} />
                </AdminAuthWrapper>
              } />
              <Route path="/admin/partners-management" element={
                <AdminAuthWrapper darkMode={darkMode}>
                  <AdminPartnersManagementPage darkMode={darkMode} />
                </AdminAuthWrapper>
              } />
              <Route path="/partner-login" element={<PartnerLoginPage darkMode={darkMode} />} />
              <Route path="/partner-dashboard" element={<PartnerDashboardPage darkMode={darkMode} />} />
              <Route path="/google-login-test" element={<GoogleLoginTestPage />} />
              <Route path="/auth-portal" element={<AuthPrototypePage />} />
              <Route path="/auth-debug" element={<AuthDebugPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage darkMode={darkMode} />} />
              <Route path="/payment-failure" element={<PaymentFailurePage darkMode={darkMode} />} />
              <Route path="/payment-test" element={<PaymentTestPage darkMode={darkMode} />} />
              <Route path="/payment-cancelled" element={<PaymentFailurePage darkMode={darkMode} />} />
              
              {/* Fallback route for 404 Not Found */}
              <Route 
                path="*" 
                element={
                  <div className={`text-center py-20 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <h1 className="text-4xl font-bold">404 - Page Not Found</h1>
                    <p className="mt-4">The page you are looking for does not exist.</p>
                  </div>
                } 
              />
            </Routes>
          </main>
          
          <Footer darkMode={darkMode} />
        </div>
      </ErrorBoundary>
    </Router>
  );
}

export default App;